/**
 * Auth context — manages Supabase session lifecycle.
 *
 * Restores session on mount, listens to auth state changes,
 * and extracts custom JWT claims (role, customer_id, trip_scope)
 * from app_metadata (server-set, cannot be spoofed by client).
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  Fragment,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { VIEW_AS_STORAGE_KEY, type Role } from '@pazportal/types';
import { useQueryClient } from '@tanstack/react-query';
import { observeAuthSession } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: Role | null;
  customerId: string | null;
  tripScope: string[] | null;
  signOut: () => Promise<boolean>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signOutError, setSignOutError] = useState(false);

  useEffect(() => observeAuthSession(supabase.auth, {
    onIdentityChange: (previous, next) => {
      queryClient.clear();
      setSignOutError(false);
      if (previous !== undefined || next === null) {
        clearViewAsSelection();
      }
    },
    onSession: (nextSession) => {
      setSession(nextSession);
      setLoading(false);
    },
  }), [queryClient]);

  const signOut = useCallback(async () => {
    setSignOutError(false);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch {
      // Supabase retains the persisted session on network/server failure.
      // Keep the UI signed in too, so a reload cannot undo an apparent logout.
      setSignOutError(true);
      return false;
    }
    queryClient.clear();
    clearViewAsSelection();
    setSession(null);
    return true;
  }, [queryClient]);

  // Extract custom claims from JWT app_metadata (set server-side, tamper-proof)
  const user = session?.user ?? null;
  const meta = user?.app_metadata ?? {};
  const role = (meta.role as Role) ?? null;
  const customerId = (meta.customer_id as string) ?? null;
  const tripScope = (meta.trip_scope as string[]) ?? null;

  return (
    <AuthContext.Provider
      value={{ user, session, loading, role, customerId, tripScope, signOut }}
    >
      <Fragment key={user?.id ?? 'signed-out'}>
        {signOutError && (
          <div role="alert" className="fixed inset-x-4 top-4 z-50 mx-auto max-w-lg rounded-md border border-notification bg-surface p-4 text-sm text-text shadow-lg">
            <p>Sign out could not be completed. You are still signed in. Check your connection and try again.</p>
            <button type="button" onClick={() => void signOut()} className="mt-2 min-h-11 font-medium underline">
              Try signing out again
            </button>
          </div>
        )}
        {children}
      </Fragment>
    </AuthContext.Provider>
  );
}

function clearViewAsSelection() {
  try {
    window.sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
    window.sessionStorage.removeItem(`${VIEW_AS_STORAGE_KEY}:recorded`);
  } catch {
    // Browsers may disable storage. Session state and query caches still reset.
  }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
